import { useCallback } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { extractSharingResource } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { componentsFactory } from '../factories';
import type { SharingModalProps } from '../types';
import { getPostTypeForTracking } from '../utils/fullPostType';

import { EXECUTORS } from './actions/executors';

type HookReturnType = {
	data: ReturnType<typeof componentsFactory>;
	actions: ReturnType<(typeof EXECUTORS)[keyof typeof EXECUTORS]> & {
		trackView: () => void;
	};
};

export const VIEW_EVENT_NAME = 'petition_manage_ready_to_share_post_modal_view';

export function useSharingModal(props: SharingModalProps): HookReturnType {
	const { translate } = useI18n();
	const track = useTracking();

	const sharingResource = extractSharingResource(props.post, {
		petition: props.petition,
		recommendedGroup: props.recommendedGroup,
	});
	const propsWithResource = { ...props, sharingResource };

	const { primaryAction, secondaryAction } = EXECUTORS[props.post.__typename](propsWithResource);

	const trackView = useCallback(() => {
		const { context, viewContext } = props.trackingProps;

		void track(VIEW_EVENT_NAME, {
			context,
			view_context: viewContext,
			location: getPostTypeForTracking(props.post),
			content: 'show_draft_post',
		});
	}, [props.trackingProps, props.post, track]);

	return {
		data: {
			...componentsFactory(propsWithResource, translate as (key: string | TranslationKey) => string),
		},
		actions: {
			primaryAction,
			secondaryAction,
			trackView,
		},
	};
}
